import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { CapaFileUpload, fileDownloadApi, pdfDownloadApi } from '@api/rest';
import { IFile } from '@routes/Common/type';
import { AxiosRequestConfig } from 'axios';
import { useCallback } from 'react';

export interface UploadedFileReturnType {
  fileExtension?: string;
  fileId: string;
  fileMimetype?: string;
  fileName: string;
  fileSize?: number | null;
  oldFileId?: string;
}

export default function useFiles() {
  const onDownloadFile = useCallback(
    async (file: IFile, config?: AxiosRequestConfig) => {
      const resp = await fileDownloadApi(file.fileId, {
        ...config
      });

      if (resp.status === 200) {
        let blob = new Blob([resp.data], {
          type: resp.headers['content-type']
        });

        if (file.fileName) {
          saveAs(blob, file.fileName);
          return true;
        }
      }
    },
    []
  );

  const onDownloadZipFile = useCallback(async (files: Array<IFile>) => {
    const zip = new JSZip();
    const zipFile = zip.folder('files');
    const list = files.map(async (file) => {
      const resp = await fileDownloadApi(file.fileId);
      let blob = new Blob([resp.data], { type: resp.headers['content-type'] });

      if (zipFile && file.fileName) {
        zipFile.file(file.fileName, blob);
      }
      return resp;
    });

    await Promise.all(list).then(() => {
      zip.generateAsync({ type: 'blob' }).then((content) => {
        saveAs(content, `files.zip`);
      });
    });
  }, []);

  const onDownloadPdf = async (
    type: 'quotation' | 'purchaseorder' | 'purchaseorder/withitem',
    fileId?: string,
    projectName?: string
  ) => {
    if (!fileId || !projectName) {
      return false;
    }

    const resp = await pdfDownloadApi(`/quotation/rfq/${type}/${fileId}`);

    if (resp.status === 200) {
      let blob = new Blob([resp.data], { type: resp.headers['content-type'] });
      const prefix = type === 'quotation' ? '견적서' : '발주서';
      saveAs(blob, `[${prefix}]${projectName}.pdf`);
      return true;
    } else {
      return false;
    }
  };

  const onUploadFiles = async (
    files: Array<File>,
    config?: AxiosRequestConfig
  ) => {
    if (files.length > 1) {
      let results: UploadedFileReturnType[] = [];
      try {
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);

          const res = await CapaFileUpload({ formData });

          results.push(res.data?.data as UploadedFileReturnType);
        }

        return {
          files: results,
          error: false
        };
      } catch {
        return {
          files: [] as UploadedFileReturnType[],
          error: true
        };
      }
    }

    if (files.length <= 1) {
      const formData = new FormData();
      formData.append('file', files[0], files[0].name);

      return CapaFileUpload({
        formData
      })
        .then((result) => {
          return {
            error: false,
            files: [
              {
                ...result.data.data,
                fileName: files[0].name
              }
            ]
          };
        })
        .catch((err) => {
          return {
            error: true,
            files: []
          };
        });
    }

    return {
      files: [],
      error: true
    };
  };

  return { onDownloadFile, onDownloadZipFile, onDownloadPdf, onUploadFiles };
}
